/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Typography,
  Box,
  Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import '../../styles/Projects.css';
import ExtendRevise from '../user/ExtendRevise';
import PastProjects from './PastProjects';

const Projects = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => {
    const path = location.pathname.split('/').pop();
    if (path === 'past') return 'past';
    if (path === 'extend') return 'extend';
    return 'ongoing';
  });
  
  // State for counts
  const [counts, setCounts] = useState({
    ongoing: 0,
    past: 0,
    extend: 0
  });

  // Fetch counts for each section
  const fetchCounts = useCallback(async () => {
    try {
      const [ongoingResponse, pastResponse, applicationsResponse] = await Promise.all([
        axios.get('/api/projects/ongoing'),
        axios.get('/api/projects/past'),
        axios.get('/api/applications/saved') // For extend/revise count
      ]);
      
      // Filter applications for revision required (extend/revise)
      const revisionRequiredApps = applicationsResponse.data.filter(app => 
        app.project_status === 'Revision Required'
      );
      
      setCounts({
        ongoing: ongoingResponse.data.length,
        past: pastResponse.data.length,
        extend: revisionRequiredApps.length
      });
    } catch (error) {
      console.error('Error fetching project counts:', error);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === 'ongoing') {
      navigate('/projects');
    } else {
      navigate(`/projects/${section}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return '#4caf50'; // Green
      case 'In Progress':
        return '#2196f3'; // Blue
      case 'Completed':
        return '#9c27b0'; // Purple
      case 'Revision Required':
        return '#ff5722'; // Deep Orange
      case 'Revised':
        return '#795548'; // Brown
      default:
        return '#757575'; // Grey
    }
  };

  const OngoingProjects = () => {
    const [projects, setProjects] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchFacilities = async () => {
      try {
        const response = await axios.get('/api/facilities');
        setFacilities(response.data);
      } catch (error) {
        setError('Failed to fetch facilities');
      }
    };

    const fetchProjects = useCallback(async () => {
      try {
        const url = selectedFacility === 'all' 
          ? '/api/projects/ongoing'
          : `/api/projects/ongoing?facility=${selectedFacility}`;
        
        const response = await axios.get(url);
        setProjects(response.data);
        setLoading(false);
      } catch (error) {
        setError('Failed to fetch projects');
        setLoading(false);
      }
    }, [selectedFacility]);

    useEffect(() => {
      fetchFacilities();
    }, []);

    useEffect(() => {
      fetchProjects();
    }, [fetchProjects]);

    const handleView = (project) => {
      navigate(`/projects/view/${project._id}`, { state: { project } });
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
      <div className="ongoing-projects">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Ongoing Projects
          </Typography>
          
          <FormControl sx={{ minWidth: 200, mb: 3 }}>
            <InputLabel>Filter by Facility</InputLabel>
            <Select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              label="Filter by Facility"
            >
              <MenuItem value="all">All Facilities</MenuItem>
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="projects table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Project Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Facility</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>PI</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map(project => (
                <TableRow 
                  key={project._id}
                  sx={{ '&:hover': { backgroundColor: '#f8f8f8' } }}
                >
                  <TableCell>{project.project_title}</TableCell>
                  <TableCell>{project.facility?.facility_name}</TableCell>
                  <TableCell>
                    {project.project_start_date ? new Date(project.project_start_date).toLocaleDateString() : 'Not set'}
                  </TableCell>
                  <TableCell>
                    {project.project_end_date ? new Date(project.project_end_date).toLocaleDateString() : 'Not set'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={project.project_status}
                      sx={{
                        backgroundColor: getStatusColor(project.project_status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {project.users?.find(user => user.role === 'PI')?.first_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleView(project)}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0'
                        }
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {projects.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            backgroundColor: '#f8f8f8',
            borderRadius: 1,
            mt: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              No ongoing projects found
            </Typography>
          </Box>
        )}
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'ongoing':
        return <OngoingProjects />;
      case 'past':
        return <PastProjects />;
      case 'extend':
        return <ExtendRevise />;
      default:
        return <OngoingProjects />;
    }
  };

  return (
    <div className="projects-container">
      <div className="sidebar">
        <h2>Projects</h2>
        <ul>
          <li>
            <button
              type="button"
              onClick={() => handleSectionChange('ongoing')}
              className={activeSection === 'ongoing' ? 'active' : ''}
              aria-current={activeSection === 'ongoing' ? 'page' : undefined}
            >
              Ongoing Projects ({counts.ongoing})
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => handleSectionChange('past')}
              className={activeSection === 'past' ? 'active' : ''}
              aria-current={activeSection === 'past' ? 'page' : undefined}
            >
              Past Projects ({counts.past})
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => handleSectionChange('extend')}
              className={activeSection === 'extend' ? 'active' : ''}
              aria-current={activeSection === 'extend' ? 'page' : undefined}
            >
              Extend/Revise ({counts.extend})
            </button>
          </li>
        </ul>
      </div>
      <div className="content-area">
        {renderSection()}
      </div>
    </div>
  );
};

export default Projects;
